import React from "react"
import SearchAndSelectMenu from "./SearchAndSelectMenu"
import { IOptionNormalized } from "../../../options/interfaces/options-normalized"
import { Grid } from "@material-ui/core"
import { useAppContext } from "../../../app-context/use-app-context"
import { getProjectsDashboardQuery } from "./utils"
import { ProjectsDashboardQueryParams } from "../../api"
import { useRouter } from "../../../hooks"
import { Alert } from "@material-ui/lab"
import { useTranslations } from "./useTranslations"
import { useOptions } from "../../../options/hooks/use-options"

const useResourceSelectionSection = ({ onSelectionChange }: Props) => {
  const { query: routerQuery } = useRouter()
  const { appContext } = useAppContext()
  const { mainContext, subContext } = appContext
  const isOrgContext = mainContext?.type === "org"
  const { customerOptions, membershipOptions, supplierOptions, workspaceOptions } = useOptions()

  let hideResourceSelection = !isOrgContext
  let hideWorkspaceSelection = !isOrgContext || workspaceOptions.length === 0
  let hideSupplierSelection = !isOrgContext || supplierOptions.length === 0
  let hideCustomerSelection = !isOrgContext || customerOptions.length === 0
  let hideMembershipSelection = !isOrgContext || membershipOptions.length === 0
  if (subContext) {
    const isWorkspaceContext = subContext.type === "workspace"
    hideResourceSelection = !isWorkspaceContext
    hideWorkspaceSelection = isWorkspaceContext
  }

  const [selectedCustomer, setSelectedCustomer] = React.useState<IOptionNormalized | null>(null)
  const [selectedMembership, setSelectedMembership] = React.useState<IOptionNormalized | null>(null)
  const [selectedSupplier, setSelectedSupplier] = React.useState<IOptionNormalized | null>(null)
  const [selectedWorkspace, setSelectedWorkspace] = React.useState<IOptionNormalized | null>(null)

  React.useEffect(() => {
    if (routerQuery) {
      setSelectedCustomer(null)
      setSelectedMembership(null)
      setSelectedSupplier(null)
      setSelectedWorkspace(null)
    }
  }, [routerQuery])

  const onCustomerSelect = (selected: IOptionNormalized | null) => {
    setSelectedCustomer(selected)
    const query = getProjectsDashboardQuery({
      appContext,
      selectedCustomer: selected,
      selectedMembership,
      selectedSupplier,
      selectedWorkspace,
    })
    onSelectionChange(query)
  }
  const onMemberSelect = (selected: IOptionNormalized | null) => {
    setSelectedMembership(selected)
    const query = getProjectsDashboardQuery({
      appContext,
      selectedCustomer,
      selectedMembership: selected,
      selectedSupplier,
      selectedWorkspace,
    })
    onSelectionChange(query)
  }
  const onSupplierSelect = (selected: IOptionNormalized | null) => {
    setSelectedSupplier(selected)
    const query = getProjectsDashboardQuery({
      appContext,
      selectedCustomer,
      selectedMembership,
      selectedSupplier: selected,
      selectedWorkspace,
    })
    onSelectionChange(query)
  }
  const onWorkspaceSelect = (selected: IOptionNormalized | null) => {
    setSelectedWorkspace(selected)
    const query = getProjectsDashboardQuery({
      appContext,
      selectedCustomer,
      selectedMembership,
      selectedSupplier,
      selectedWorkspace: selected,
    })
    onSelectionChange(query)
  }

  return {
    customerOptions,
    membershipOptions,
    supplierOptions,
    workspaceOptions,
    hideResourceSelection,
    hideWorkspaceSelection,
    hideSupplierSelection,
    hideCustomerSelection,
    hideMembershipSelection,
    onCustomerSelect,
    onMemberSelect,
    onSupplierSelect,
    onWorkspaceSelect,
    selectedCustomer,
    selectedMembership,
    selectedSupplier,
    selectedWorkspace,
  }
}

const ResourceSelectionSection = (props: Props) => {
  const {
    customerOptions,
    membershipOptions,
    supplierOptions,
    workspaceOptions,
    hideResourceSelection,
    hideWorkspaceSelection,
    hideSupplierSelection,
    hideCustomerSelection,
    hideMembershipSelection,
    onCustomerSelect,
    onMemberSelect,
    onSupplierSelect,
    onWorkspaceSelect,
    selectedCustomer,
    selectedMembership,
    selectedSupplier,
    selectedWorkspace,
  } = useResourceSelectionSection(props)

  const displayFiltersNote = selectedCustomer || selectedMembership || selectedSupplier || selectedWorkspace
  const appliedFilters = [
    selectedCustomer?.name,
    selectedMembership?.name,
    selectedWorkspace?.name,
    selectedSupplier?.name,
  ]
    .filter(Boolean)
    .join(", ")
  const translations = useTranslations({ filters: appliedFilters })

  if (hideResourceSelection) return null

  return (
    <Grid container direction="column" spacing={2}>
      <Grid container spacing={2}>
        {!hideCustomerSelection && (
          <Grid item xs={12} sm={6} md={3}>
            <SearchAndSelectMenu
              label={translations.customersFilterLabel}
              value={selectedCustomer}
              options={customerOptions}
              onSelect={onCustomerSelect}
            />
          </Grid>
        )}
        {!hideMembershipSelection && (
          <Grid item xs={12} sm={6} md={3}>
            <SearchAndSelectMenu
              label={translations.membershipsFilterLabel}
              value={selectedMembership}
              options={membershipOptions}
              onSelect={onMemberSelect}
            />
          </Grid>
        )}
        {!hideWorkspaceSelection && (
          <Grid item xs={12} sm={6} md={3}>
            <SearchAndSelectMenu
              label={translations.workspacesFilterLabel}
              value={selectedWorkspace}
              options={workspaceOptions}
              onSelect={onWorkspaceSelect}
            />
          </Grid>
        )}
        {!hideSupplierSelection && (
          <Grid item xs={12} sm={6} md={3}>
            <SearchAndSelectMenu
              label={translations.suppliersFilterLabel}
              value={selectedSupplier}
              options={supplierOptions}
              onSelect={onSupplierSelect}
            />
          </Grid>
        )}
      </Grid>
      <Grid item>{displayFiltersNote && <Alert severity="info">{translations.appliedFiltersLabel}</Alert>}</Grid>
    </Grid>
  )
}

export default ResourceSelectionSection
type Props = {
  onSelectionChange: (query: ProjectsDashboardQueryParams) => void
}
